import { http } from '@/lib/api'
import type {
  BulkTransitionAction,
  BulkTransitionResponse,
  CustomInvoiceInput,
  DistributionLine,
  GenerateFromHoursInput,
  Invoice,
  InvoiceClientOption,
  InvoiceComment,
  InvoiceListItem,
  InvoiceListParams,
  MonthlyPreviewInput,
  MonthlyPreviewResponse,
  MyPayoutLine,
  PaymentInput,
  PayoutInput,
  SendInput,
  SetDistributionInput,
  TemplateRef,
  UpdateDraftInput,
} from '@/lib/invoice-types'
import type { CorrectionResult, InvoiceApi, WithdrawResult } from './adapter'

export class RealInvoiceApi implements InvoiceApi {
  list(params: InvoiceListParams) {
    return http.get<InvoiceListItem[]>('/api/invoices', {
      status: params.status,
      statuses: params.statuses?.length ? params.statuses.join(',') : undefined,
      attention: params.attention ? '1' : undefined,
      clientId: params.clientId,
      year: params.year,
      search: params.search,
    })
  }

  get(id: string) {
    return http.get<Invoice>(`/api/invoices/${id}`)
  }

  customPreviewHtml(input: CustomInvoiceInput) {
    return http.post<string>('/api/invoices/custom/preview-html', input)
  }

  templates() {
    return http.get<TemplateRef[]>('/api/invoice-templates')
  }

  clients() {
    return http.get<InvoiceClientOption[]>('/api/clients', { all: 1 })
  }

  myPayouts() {
    return http.get<MyPayoutLine[]>('/api/invoices/my-payouts')
  }

  previewFromHours(input: MonthlyPreviewInput) {
    return http.post<MonthlyPreviewResponse>('/api/invoices/preview-from-hours', input)
  }

  generateFromHours(input: GenerateFromHoursInput) {
    return http.post<{ created: InvoiceListItem[]; warnings: string[] }>('/api/invoices/from-hours', input)
  }

  createCustom(input: CustomInvoiceInput) {
    return http.post<Invoice>('/api/invoices/custom', input)
  }

  updateDraft(id: string, input: UpdateDraftInput) {
    return http.patch<Invoice>(`/api/invoices/${id}`, input)
  }

  setNotes(id: string, notes: string | null) {
    return http.put<Invoice>(`/api/invoices/${id}/notes`, { notes })
  }

  setDistribution(id: string, input: SetDistributionInput) {
    return http.put<Invoice>(`/api/invoices/${id}/distribution`, input)
  }

  clearDistribution(id: string) {
    return http.del<Invoice>(`/api/invoices/${id}/distribution`)
  }

  recalculate(id: string) {
    return http.post<Invoice>(`/api/invoices/${id}/recalculate`)
  }

  submit(id: string) {
    return http.post<Invoice>(`/api/invoices/${id}/submit`)
  }

  remove(id: string) {
    return http.del<void>(`/api/invoices/${id}`).then(() => undefined)
  }

  park(id: string, input: { releaseDate?: string | null; reason?: string | null }) {
    return http.post<Invoice>(`/api/invoices/${id}/park`, {
      releaseDate: input.releaseDate ?? undefined,
      reason: input.reason ?? undefined,
    })
  }

  release(id: string) {
    return http.post<Invoice>(`/api/invoices/${id}/release`)
  }

  bulkTransition(input: { ids: string[]; action: BulkTransitionAction; issueDate?: string | null }) {
    return http.post<BulkTransitionResponse>('/api/invoices/bulk-transition', {
      ids: input.ids,
      action: input.action,
      issueDate: input.issueDate ?? undefined,
    })
  }

  approve(id: string, issueDate?: string | null) {
    return http.post<Invoice>(`/api/invoices/${id}/approve`, issueDate ? { issueDate } : {})
  }

  reject(id: string, comment: string) {
    return http.post<Invoice>(`/api/invoices/${id}/reject`, { comment })
  }

  send(id: string, input: SendInput) {
    return http.post<Invoice>(`/api/invoices/${id}/send`, input)
  }

  sendReminder(id: string, input: SendInput) {
    return http.post<Invoice>(`/api/invoices/${id}/reminder`, input)
  }

  markSent(id: string) {
    return http.post<Invoice>(`/api/invoices/${id}/mark-sent`)
  }

  registerPayment(id: string, input: PaymentInput) {
    return http.post<Invoice>(`/api/invoices/${id}/payment`, input)
  }

  unapprove(id: string, reason: string) {
    return http.post<Invoice>(`/api/invoices/${id}/unapprove`, { reason })
  }

  voidInvoice(id: string, reason: string) {
    return http.post<Invoice>(`/api/invoices/${id}/void`, { reason })
  }

  escalate(id: string, reason: string) {
    return http.post<Invoice>(`/api/invoices/${id}/escalate`, { reason })
  }

  deEscalate(id: string) {
    return http.post<Invoice>(`/api/invoices/${id}/de-escalate`)
  }

  withdraw(id: string, input: { reason: string; createCredit: boolean }) {
    return http.post<WithdrawResult>(`/api/invoices/${id}/withdraw`, {
      reason: input.reason,
      createCredit: input.createCredit,
    })
  }

  writeOff(id: string, reason: string) {
    return http.post<Invoice>(`/api/invoices/${id}/write-off`, { reason })
  }

  reopen(id: string) {
    return http.post<Invoice>(`/api/invoices/${id}/reopen`)
  }

  correct(id: string) {
    return http.post<CorrectionResult>(`/api/invoices/${id}/correct`)
  }

  credit(id: string) {
    return http.post<Invoice>(`/api/invoices/${id}/credit`)
  }


  setPayout(id: string, lineId: string, input: PayoutInput) {
    return http.patch<DistributionLine>(`/api/invoices/${id}/distribution/${lineId}/payout`, input)
  }

  addComment(id: string, body: string) {
    return http.post<InvoiceComment>(`/api/invoices/${id}/comments`, { body })
  }
}
